import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { SprintCompletionModal } from '../components/SprintCompletionModal';
import * as taskboardService from '../services/taskboardService';
import * as requirementService from '../services/requirementService';
import { listUsers, getUserPreferences, updateUserPreferences } from '../services/userService';
import * as teamService from '../services/teamService';

const STORY_TYPES = ['bug', 'task', 'spike'];
const PRIORITIES = ['critical', 'high', 'medium', 'low'];

export function TaskBoardBacklog(): React.ReactElement {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isManager = user?.role === 'platform_admin' || user?.role === 'company_admin' || user?.role === 'project_manager' || user?.role === 'project_manager_lite';

  // State
  const [projects, setProjects] = useState<any[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState(searchParams.get('project_id') || '');
  const [backlog, setBacklog] = useState<any[]>([]);
  const [sprints, setSprints] = useState<any[]>([]);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [selectedSprintItems, setSelectedSprintItems] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  // Sprint creation (sprints are company-wide, not project-specific)
  const [showCreateSprint, setShowCreateSprint] = useState(false);
  const [newSprintName, setNewSprintName] = useState('');
  const [newSprintGoal, setNewSprintGoal] = useState('');
  const [newSprintStart, setNewSprintStart] = useState('');
  const [newSprintEnd, setNewSprintEnd] = useState('');
  const [newSprintTeamId, setNewSprintTeamId] = useState('');
  const [creatingSprint, setCreatingSprint] = useState(false);

  // Sprint editing
  const [editingSprint, setEditingSprint] = useState<any>(null);
  const [editSprintName, setEditSprintName] = useState('');
  const [editSprintGoal, setEditSprintGoal] = useState('');
  const [editSprintStart, setEditSprintStart] = useState('');
  const [editSprintEnd, setEditSprintEnd] = useState('');
  const [editSprintTeamId, setEditSprintTeamId] = useState('');
  const [updatingSprint, setUpdatingSprint] = useState(false);

  // Sprint completion
  const [completingSprint, setCompletingSprint] = useState<any>(null);
  const [sprintItems, setSprintItems] = useState<any[]>([]);

  // Company users for assignee dropdown
  const [companyUsers, setCompanyUsers] = useState<any[]>([]);

  // Sprint items by sprint ID
  const [sprintItemsMap, setSprintItemsMap] = useState<Record<string, any[]>>({});

  // Filters
  const [filterPriority, setFilterPriority] = useState('');
  const [filterStoryType, setFilterStoryType] = useState('');
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [overdueOnly, setOverdueOnly] = useState(false);

  // Quick-add
  const [quickAddActiveSection, setQuickAddActiveSection] = useState<string | null>(null);
  const [quickAddTitle, setQuickAddTitle] = useState('');
  const [quickAddProjectId, setQuickAddProjectId] = useState('');
  const [quickAddSubmitting, setQuickAddSubmitting] = useState(false);

  // Inline editing
  const [editingField, setEditingField] = useState<{ id: string; field: string } | null>(null);

  // Reorder feedback
  const [reorderMessage, setReorderMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Action dropdown
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const dropdownButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; title: string; projectId: string } | null>(null);

  // Team filter
  const [teams, setTeams] = useState<teamService.Team[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [teamMemberIds, setTeamMemberIds] = useState<Set<string>>(new Set());

  // Load projects - for backlog, we want all projects with approved requirements
  useEffect(() => {
    async function loadProjects() {
      if (!token) return;
      try {
        const res = await taskboardService.getTaskboardProjects(token, true); // forBacklog=true
        setProjects(res.data || []);
      } catch (err) {
        console.error('Error loading projects:', err);
      }
    }
    loadProjects();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load teams and saved team preference
  useEffect(() => {
    async function loadTeams() {
      if (!token || !isManager) return;
      try {
        const [teamsList, prefs] = await Promise.all([
          teamService.listTeams(token),
          getUserPreferences(token)
        ]);
        setTeams(teamsList);
        const savedTeamId = prefs?.selectedTeamId;
        if (savedTeamId && teamsList.some((t: teamService.Team) => t.id === savedTeamId)) {
          setSelectedTeamId(savedTeamId);
          const members = await teamService.getTeamById(savedTeamId, token);
          setTeamMemberIds(new Set(members.members.map((m: teamService.TeamMember) => m.user_id)));
        }
      } catch (err) {
        console.error('Error loading teams:', err);
      }
    }
    loadTeams();
  }, [token, isManager]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle team change
  const handleTeamChange = useCallback(async (teamId: string) => {
    if (!token) return;
    setSelectedTeamId(teamId);
    try {
      await updateUserPreferences({ selectedTeamId: teamId || null }, token);
      if (teamId) {
        const teamData = await teamService.getTeamById(teamId, token);
        setTeamMemberIds(new Set(teamData.members.map((m: teamService.TeamMember) => m.user_id)));
      } else {
        setTeamMemberIds(new Set());
      }
    } catch (err) {
      console.error('Error changing team:', err);
    }
  }, [token]);

  // Load backlog and sprints
  const loadData = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [backlogRes, sprintsRes, usersRes] = await Promise.all([
        taskboardService.getBacklog(token, selectedProjectId || undefined),
        taskboardService.getSprints(selectedProjectId || '', token),
        listUsers({ page: 1, limit: 500, status: 'active' }, token).catch(() => ({ users: [] })),
      ]);
      setBacklog(backlogRes.data || []);
      setSprints(sprintsRes.data || []);
      setCompanyUsers(usersRes.users || []);

      // Load items for each non-completed sprint
      const sprintsList = sprintsRes.data || [];
      const itemsMap: Record<string, any[]> = {};
      for (const sprint of sprintsList) {
        if (sprint.status !== 'completed') {
          try {
            const boardRes = await taskboardService.getBoardData(sprint.project_id || selectedProjectId || '', token, sprint.id);
            const allItems = (boardRes.data?.columns || []).flatMap((c: any) => c.requirements || []);
            itemsMap[sprint.id] = allItems;
          } catch (e) {
            itemsMap[sprint.id] = [];
          }
        }
      }
      setSprintItemsMap(itemsMap);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  }, [token, selectedProjectId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Click-outside handler for action dropdown
  useEffect(() => {
    if (!openDropdownId) return;
    function handleMouseDown(e: MouseEvent) {
      const target = e.target as HTMLElement;
      if (!target.closest('.action-dropdown-menu') && !target.closest('.action-dropdown-btn')) {
        setOpenDropdownId(null);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [openDropdownId]);

  // Position calculator for action dropdown (fixed positioning to escape overflow:hidden)
  useEffect(() => {
    if (!openDropdownId) return;
    const btn = dropdownButtonRefs.current[openDropdownId];
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const menuWidth = 208; // w-52 = 13rem = 208px
    if (spaceBelow < 250) {
      setDropdownStyle({ position: 'fixed', bottom: window.innerHeight - rect.top + 4, right: window.innerWidth - rect.right, width: menuWidth });
    } else {
      setDropdownStyle({ position: 'fixed', top: rect.bottom + 4, right: window.innerWidth - rect.right, width: menuWidth });
    }
  }, [openDropdownId]);

  // Build deduplicated person list from backlog + sprint items (assignees and creators)
  const persons = useMemo(() => {
    const personMap = new Map<string, { id: string; name: string; initials: string }>();

    function addPerson(id: string | null, name: string | null) {
      if (!id || !name || personMap.has(id)) return;
      const nameParts = name.trim().split(' ');
      const initials = nameParts.length >= 2
        ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase()
        : nameParts[0].substring(0, 2).toUpperCase();
      personMap.set(id, { id, name, initials });
    }

    // From backlog items
    backlog.forEach(item => {
      addPerson(item.assigned_to, item.assigned_to_name);
      addPerson(item.created_by, item.created_by_name);
    });

    // From sprint items
    Object.values(sprintItemsMap).forEach((items: any[]) => {
      items.forEach(item => {
        addPerson(item.assigned_to, item.assigned_to_name);
        if (item.created_by) {
          const creatorName = item.created_by_name || (() => {
            const u = companyUsers.find(u => u.id === item.created_by);
            return u ? `${u.first_name} ${u.last_name}` : null;
          })();
          addPerson(item.created_by, creatorName);
        }
      });
    });

    return Array.from(personMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [backlog, sprintItemsMap, companyUsers]);

  // Filtered backlog
  const now = useMemo(() => new Date(), [overdueOnly]); // eslint-disable-line react-hooks/exhaustive-deps
  const filteredBacklog = backlog.filter(item => {
    if (selectedTeamId && teamMemberIds.size > 0 && !teamMemberIds.has(item.assigned_to) && !teamMemberIds.has(item.created_by)) return false;
    if (filterPriority && item.priority !== filterPriority) return false;
    if (filterStoryType && item.story_type !== filterStoryType) return false;
    if (selectedPersonId && item.assigned_to !== selectedPersonId && item.created_by !== selectedPersonId) return false;
    if (overdueOnly && !(item.due_date && new Date(item.due_date) < now)) return false;
    return true;
  });

  // Filtered sprint items map
  const filteredSprintItemsMap = useMemo(() => {
    if (!selectedPersonId && !overdueOnly && !selectedTeamId && !selectedProjectId) return sprintItemsMap;
    const filtered: Record<string, any[]> = {};
    for (const [sprintId, items] of Object.entries(sprintItemsMap)) {
      filtered[sprintId] = items.filter((item: any) => {
        if (selectedProjectId && item.project_id !== selectedProjectId) return false;
        if (selectedTeamId && teamMemberIds.size > 0 && !teamMemberIds.has(item.assigned_to) && !teamMemberIds.has(item.created_by)) return false;
        if (selectedPersonId && item.assigned_to !== selectedPersonId && item.created_by !== selectedPersonId) return false;
        if (overdueOnly && !(item.due_date && new Date(item.due_date) < now)) return false;
        return true;
      });
    }
    return filtered;
  }, [sprintItemsMap, selectedProjectId, selectedPersonId, overdueOnly, now, selectedTeamId, teamMemberIds]);

  // Selection helpers
  function toggleSelection(id: string) {
    const next = new Set(selectedItems);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedItems(next);
  }

  function toggleSelectAll() {
    if (selectedItems.size === filteredBacklog.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredBacklog.map(r => r.id)));
    }
  }

  // Sprint actions (sprints are company-wide, project_id is optional)
  async function handleCreateSprint() {
    if (!token || !newSprintName || !newSprintStart || !newSprintEnd) return;
    setCreatingSprint(true);
    try {
      await taskboardService.createSprint({
        project_id: '', // Sprints are company-wide, no project required
        team_id: newSprintTeamId || undefined,
        name: newSprintName,
        goal: newSprintGoal || undefined,
        start_date: newSprintStart,
        end_date: newSprintEnd,
      }, token);
      setShowCreateSprint(false);
      setNewSprintName('');
      setNewSprintGoal('');
      setNewSprintStart('');
      setNewSprintEnd('');
      setNewSprintTeamId('');
      loadData();
    } catch (err: any) {
      console.error('Error creating sprint:', err);
    } finally {
      setCreatingSprint(false);
    }
  }

  async function handleStartSprint(sprintId: string) {
    if (!token) return;
    try {
      await taskboardService.startSprint(sprintId, token);
      loadData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to start sprint');
    }
  }

  async function handleAddToSprint(sprintId: string) {
    if (!token || selectedItems.size === 0) return;
    try {
      await taskboardService.addToSprint(Array.from(selectedItems), sprintId, token);
      setSelectedItems(new Set());
      loadData();
    } catch (err) {
      console.error('Error adding to sprint:', err);
    }
  }

  async function handleRemoveFromSprint() {
    if (!token || selectedSprintItems.size === 0) return;
    try {
      await taskboardService.removeFromSprint(Array.from(selectedSprintItems), token);
      setSelectedSprintItems(new Set());
      loadData();
    } catch (err) {
      console.error('Error removing from sprint:', err);
    }
  }

  function toggleSprintItemSelection(id: string) {
    const next = new Set(selectedSprintItems);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedSprintItems(next);
  }

  function toggleSelectAllSprintItems(sprintId: string) {
    const items = sprintItemsMap[sprintId] || [];
    const allSelected = items.length > 0 && items.every((item: any) => selectedSprintItems.has(item.id));
    if (allSelected) {
      const next = new Set(selectedSprintItems);
      items.forEach((item: any) => next.delete(item.id));
      setSelectedSprintItems(next);
    } else {
      const next = new Set(selectedSprintItems);
      items.forEach((item: any) => next.add(item.id));
      setSelectedSprintItems(next);
    }
  }

  function getSelectedSprintItemCount(sprintId: string): number {
    const items = sprintItemsMap[sprintId] || [];
    return items.filter((item: any) => selectedSprintItems.has(item.id)).length;
  }

  async function handleDeleteSprint(sprintId: string) {
    if (!token || !window.confirm('Delete this sprint? Items will be moved to backlog.')) return;
    try {
      await taskboardService.deleteSprint(sprintId, token);
      loadData();
    } catch (err) {
      console.error('Error deleting sprint:', err);
    }
  }

  function handleEditSprint(sprint: any) {
    setEditingSprint(sprint);
    setEditSprintName(sprint.name);
    setEditSprintGoal(sprint.goal || '');
    setEditSprintStart(sprint.start_date ? new Date(sprint.start_date).toISOString().split('T')[0] : '');
    setEditSprintEnd(sprint.end_date ? new Date(sprint.end_date).toISOString().split('T')[0] : '');
    setEditSprintTeamId(sprint.team_id || '');
  }

  async function handleUpdateSprint() {
    if (!token || !editingSprint || !editSprintName) return;
    setUpdatingSprint(true);
    try {
      const data: any = { name: editSprintName, goal: editSprintGoal || undefined, team_id: editSprintTeamId || null };
      if (editingSprint.status === 'planning') {
        data.start_date = editSprintStart;
        data.end_date = editSprintEnd;
      }
      await taskboardService.updateSprint(editingSprint.id, data, token);
      setEditingSprint(null);
      loadData();
    } catch (err: any) {
      console.error('Error updating sprint:', err);
      alert(err.response?.data?.error || 'Failed to update sprint');
    } finally {
      setUpdatingSprint(false);
    }
  }

  async function handleCompleteSprint(sprint: any) {
    if (!token) return;
    try {
      // Load sprint items to find incomplete ones
      const boardRes = await taskboardService.getBoardData(selectedProjectId, token, sprint.id);
      const allItems = (boardRes.data?.columns || []).flatMap((c: any) => c.requirements || []);
      // Get columns to determine done types
      const doneColumns = (boardRes.data?.columns || [])
        .filter((c: any) => c.column_type === 'done' || c.column_type === 'done_not_worked')
        .map((c: any) => c.id);
      const incomplete = allItems.filter((r: any) => !doneColumns.includes(r.board_column_id));
      setSprintItems(incomplete);
      setCompletingSprint(sprint);
    } catch (err) {
      console.error('Error:', err);
    }
  }

  async function handleInlineEdit(id: string, field: string, value: any) {
    if (!token) return;
    try {
      const data: any = {};
      data[field] = value;
      await taskboardService.assignRequirement(id, data, token);
      setEditingField(null);

      // Targeted state update instead of full loadData() to avoid unmounting DragDropContext
      const updateItem = (item: any) => {
        if (item.id !== id) return item;
        const updated = { ...item, [field]: value };
        if (field === 'assigned_to') {
          const u = companyUsers.find(u => u.id === value);
          updated.assigned_to_name = u ? `${u.first_name} ${u.last_name}` : null;
        }
        return updated;
      };

      setBacklog(prev => prev.map(updateItem));
      setSprintItemsMap(prev => {
        const updated: Record<string, any[]> = {};
        for (const [sprintId, items] of Object.entries(prev)) {
          updated[sprintId] = items.map(updateItem);
        }
        return updated;
      });
    } catch (err) {
      console.error('Error updating:', err);
    }
  }

  function renderStoryTypeIcon(storyType: string | null) {
    if (!storyType) return <span className="text-xs text-gray-400">—</span>;
    const iconClass = 'w-4 h-4 flex-shrink-0';
    let icon = null;
    switch (storyType) {
      case 'story':
        icon = <svg className={`${iconClass} text-green-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" /></svg>;
        break;
      case 'bug':
        icon = <svg className={`${iconClass} text-red-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>;
        break;
      case 'task':
        icon = <svg className={`${iconClass} text-blue-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>;
        break;
      case 'epic':
        icon = <svg className={`${iconClass} text-purple-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>;
        break;
      case 'spike':
        icon = <svg className={`${iconClass} text-yellow-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>;
        break;
      case 'sub-task':
        icon = <svg className={`${iconClass} text-gray-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>;
        break;
    }
    return (
      <span className="inline-flex items-center gap-1">
        {icon}
        <span className="text-xs text-gray-600 dark:text-gray-400 capitalize">{storyType.replace('-', ' ')}</span>
      </span>
    );
  }

  const activeSprint = sprints.find(s => s.status === 'active');
  const planningSprints = sprints.filter(s => s.status === 'planning');

  // Handle drag end for reordering
  async function handleDragEnd(result: DropResult) {
    if (!result.destination || !token) return;

    const { source, destination } = result;

    // If dropped in the same position, do nothing
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    // Clear any inline editing that might trigger loadData via onBlur
    setEditingField(null);

    // Handle backlog reordering
    if (source.droppableId === 'backlog' && destination.droppableId === 'backlog') {
      const newBacklog = Array.from(filteredBacklog);
      const [removed] = newBacklog.splice(source.index, 1);
      newBacklog.splice(destination.index, 0, removed);

      // Save previous state for rollback
      const prevBacklog = [...backlog];

      // Optimistic update
      setBacklog(prev => {
        const unfilteredItems = prev.filter(item => !filteredBacklog.find(f => f.id === item.id));
        return [...newBacklog, ...unfilteredItems];
      });

      // Update order on backend
      try {
        const res = await taskboardService.updateBacklogOrder(
          newBacklog.map((item, idx) => ({ id: item.id, order: idx })),
          token
        );
        console.log('Backlog reorder API response:', res);
        setReorderMessage({ text: 'Order saved', type: 'success' });
      } catch (err: any) {
        console.error('Error reordering backlog:', err?.response?.data || err?.message || err);
        setReorderMessage({ text: `Reorder failed: ${err?.response?.data?.error || err?.message || 'Unknown error'}`, type: 'error' });
        setBacklog(prevBacklog);
      }
      setTimeout(() => setReorderMessage(null), 3000);
      return;
    }

    // Handle sprint items reordering
    if (source.droppableId.startsWith('sprint-') && destination.droppableId === source.droppableId) {
      const sprintId = source.droppableId.replace('sprint-', '');
      const currentItems = sprintItemsMap[sprintId] || [];
      const newItems = Array.from(currentItems);
      const [removed] = newItems.splice(source.index, 1);
      newItems.splice(destination.index, 0, removed);

      // Save previous state for rollback
      const prevItems = [...currentItems];

      // Optimistic update
      setSprintItemsMap(prev => ({
        ...prev,
        [sprintId]: newItems
      }));

      // Update order on backend
      try {
        const res = await taskboardService.updateSprintOrder(
          sprintId,
          newItems.map((item, idx) => ({ id: item.id, order: idx })),
          token
        );
        console.log('Sprint reorder API response:', res);
        setReorderMessage({ text: 'Order saved', type: 'success' });
      } catch (err: any) {
        console.error('Error reordering sprint items:', err?.response?.data || err?.message || err);
        setReorderMessage({ text: `Reorder failed: ${err?.response?.data?.error || err?.message || 'Unknown error'}`, type: 'error' });
        setSprintItemsMap(prev => ({
          ...prev,
          [sprintId]: prevItems
        }));
      }
      setTimeout(() => setReorderMessage(null), 3000);
      return;
    }

    // If we get here, the drag was between incompatible droppables
    console.log('Drag between incompatible droppables:', source.droppableId, '->', destination.droppableId);
  }

  // Get assignee name helper
  function getAssigneeName(assignedTo: string | null): string {
    if (!assignedTo) return 'Unassigned';
    const user = companyUsers.find(u => u.id === assignedTo);
    return user ? `${user.first_name} ${user.last_name}` : 'Unassigned';
  }

  // Quick-add handlers
  function handleQuickAddActivate(section: string) {
    setQuickAddActiveSection(section);
    setQuickAddTitle('');
    setQuickAddProjectId(selectedProjectId || (projects.length > 0 ? projects[0].id : ''));
  }

  function handleQuickAddCancel() {
    setQuickAddActiveSection(null);
    setQuickAddTitle('');
  }

  async function handleQuickAddSubmit(section: string) {
    const projectId = selectedProjectId || quickAddProjectId;
    if (!token || !quickAddTitle.trim() || !projectId) return;
    setQuickAddSubmitting(true);
    try {
      const created = await requirementService.createRequirement(projectId, {
        title: quickAddTitle.trim(),
        requirement_type: 'functional',
        priority: 'medium',
        hierarchy_type: 'parent',
        approval_status: 'approved',
      }, token);

      // If this is a sprint section, also add to that sprint
      if (section !== 'backlog') {
        await taskboardService.addToSprint([created.id], section, token);
      }

      setQuickAddTitle('');
      setQuickAddActiveSection(null);
      loadData();
    } catch (err) {
      console.error('Error creating task:', err);
    } finally {
      setQuickAddSubmitting(false);
    }
  }

  function handleQuickAddKeyDown(e: React.KeyboardEvent, section: string) {
    if (e.key === 'Enter' && !quickAddSubmitting) {
      e.preventDefault();
      handleQuickAddSubmit(section);
    } else if (e.key === 'Escape') {
      handleQuickAddCancel();
    }
  }

  // Action dropdown handlers
  function handleActionDelete(item: any) {
    setOpenDropdownId(null);
    setConfirmDelete({ id: item.id, title: item.title, projectId: item.project_id });
  }

  async function handleConfirmDelete() {
    if (!confirmDelete || !token) return;
    try {
      await requirementService.deleteRequirement(confirmDelete.projectId, confirmDelete.id, token);
      setConfirmDelete(null);
      loadData();
    } catch (err) {
      console.error('Error deleting requirement:', err);
    }
  }

  async function handleActionMoveToSprint(itemId: string, sprintId: string) {
    if (!token) return;
    setOpenDropdownId(null);
    try {
      await taskboardService.addToSprint([itemId], sprintId, token);
      loadData();
    } catch (err) {
      console.error('Error moving to sprint:', err);
    }
  }

  async function handleActionVerifyTask(item: any) {
    if (!token) return;
    setOpenDropdownId(null);
    try {
      await taskboardService.assignRequirement(item.id, { is_verified: true }, token);
      loadData();
    } catch (err) {
      console.error('Error verifying task:', err);
    }
  }

  async function handleActionChangeToPending(item: any) {
    if (!token) return;
    setOpenDropdownId(null);
    try {
      await requirementService.updateRequirement(item.project_id, item.id, { approval_status: 'pending' }, token);
      loadData();
    } catch (err) {
      console.error('Error changing to pending:', err);
    }
  }

  async function handleActionMoveToBacklog(itemId: string) {
    if (!token) return;
    setOpenDropdownId(null);
    try {
      await taskboardService.removeFromSprint([itemId], token);
      loadData();
    } catch (err) {
      console.error('Error moving to backlog:', err);
    }
  }

  function getEligibleSprints(excludeSprintId?: string) {
    return sprints.filter(s => s.status !== 'completed' && s.id !== excludeSprintId);
  }

  function renderActionDropdown(item: any, currentSprintId?: string) {
    const isOpen = openDropdownId === item.id;
    const eligible = getEligibleSprints(currentSprintId);
    return (
      <td className="px-2 py-1.5">
        <button
          ref={el => { dropdownButtonRefs.current[item.id] = el; }}
          className="action-dropdown-btn p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          onClick={e => { e.stopPropagation(); setOpenDropdownId(isOpen ? null : item.id); }}
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
          </svg>
        </button>
        {isOpen && (
          <div
            className="action-dropdown-menu bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 z-[9999]"
            style={dropdownStyle}
            onClick={e => e.stopPropagation()}
          >
            {eligible.length > 0 && (
              <>
                <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">Move to Sprint</div>
                {eligible.map(s => (
                  <button
                    key={s.id}
                    onClick={e => { e.stopPropagation(); handleActionMoveToSprint(item.id, s.id); }}
                    className="w-full text-left px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:text-indigo-700 dark:hover:text-indigo-400"
                  >
                    {s.name}
                  </button>
                ))}
              </>
            )}
            {currentSprintId && isManager && (
              <button
                onClick={e => { e.stopPropagation(); handleActionMoveToBacklog(item.id); }}
                className="w-full text-left px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-700 dark:hover:text-blue-400"
              >
                Move to Backlog
              </button>
            )}
            {isManager && item.is_verified === false && (
              <>
                <div className="border-t border-gray-100 dark:border-gray-700 my-1"></div>
                <button
                  onClick={e => { e.stopPropagation(); handleActionVerifyTask(item); }}
                  className="w-full text-left px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-green-50 dark:hover:bg-green-900/30 hover:text-green-700 dark:hover:text-green-400"
                >
                  Verify Task
                </button>
              </>
            )}
            <div className="border-t border-gray-100 dark:border-gray-700 my-1"></div>
            <button
              onClick={e => { e.stopPropagation(); handleActionChangeToPending(item); }}
              className="w-full text-left px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/30 hover:text-yellow-700 dark:hover:text-yellow-400"
            >
              Change to Pending
            </button>
            <div className="border-t border-gray-100 dark:border-gray-700 my-1"></div>
            <button
              onClick={e => { e.stopPropagation(); handleActionDelete(item); }}
              className="w-full text-left px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
            >
              Delete
            </button>
          </div>
        )}
      </td>
    );
  }

  return (
    <Layout>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="p-6 pb-0">
          <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-400 rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Task Board - Backlog</h1>
                <div className="flex items-center gap-3">
                  {/* Tab toggle */}
                  <div className="flex bg-white/20 rounded-lg p-0.5">
                    <button
                      onClick={() => navigate('/taskboard' + (selectedProjectId ? `?project_id=${selectedProjectId}` : ''))}
                      className="px-3 py-1.5 text-sm font-medium rounded-md text-blue-100 hover:text-white transition-colors"
                    >
                      Board
                    </button>
                    <button
                      className="px-3 py-1.5 text-sm font-medium rounded-md bg-white text-indigo-600 shadow-sm transition-colors"
                    >
                      Backlog
                    </button>
                  </div>
                  {/* Person filter avatars */}
                  {persons.length > 0 && (
                    <div className="flex items-center gap-1.5 ml-8">
                      {persons.map(person => (
                        <button
                          key={person.id}
                          onClick={() => setSelectedPersonId(
                            selectedPersonId === person.id ? null : person.id
                          )}
                          title={`${person.name}${selectedPersonId === person.id ? ' (Click to clear filter)' : ''}`}
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                            selectedPersonId === person.id
                              ? 'bg-white text-indigo-600 ring-2 ring-white ring-offset-2 ring-offset-indigo-600 scale-110'
                              : selectedPersonId
                                ? 'bg-white/30 text-white/60 hover:bg-white/50'
                                : 'bg-white/20 text-white hover:bg-white/40'
                          }`}
                        >
                          {person.initials}
                        </button>
                      ))}
                      {selectedPersonId && (
                        <button
                          onClick={() => setSelectedPersonId(null)}
                          title="Clear filter"
                          className="ml-1 w-6 h-6 rounded-full bg-white/20 text-white hover:bg-white/40 flex items-center justify-center transition-colors"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}
                  {/* Overdue filter */}
                  <label className="flex items-center gap-2 ml-6 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={overdueOnly}
                      onChange={e => setOverdueOnly(e.target.checked)}
                      className="rounded text-red-500 focus:ring-red-400 border-white/30 bg-white/20"
                    />
                    <span className="text-sm text-white font-medium">Overdue Only</span>
                  </label>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* Team dropdown */}
                {isManager && teams.length > 0 && (
                  <select
                    value={selectedTeamId}
                    onChange={e => handleTeamChange(e.target.value)}
                    className="min-w-[140px] max-w-[200px] pl-3 pr-8 py-2 border border-gray-300 bg-white rounded-md text-sm text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:outline-none shadow-sm cursor-pointer truncate"
                  >
                    <option value="">All Teams</option>
                    {teams.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                )}

                <select
                  value={selectedProjectId}
                  onChange={e => { setSelectedProjectId(e.target.value); setSelectedItems(new Set()); setSelectedSprintItems(new Set()); }}
                  className="min-w-[180px] max-w-[280px] pl-3 pr-8 py-2 border border-gray-300 bg-white rounded-md text-sm text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:outline-none shadow-sm cursor-pointer truncate"
                >
                  <option value="">All Projects</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.project_code} - {p.name}</option>
                  ))}
                </select>

                {isManager && (
                  <button
                    onClick={() => setShowCreateSprint(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 shadow-sm transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create Sprint
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Active filter indicators */}
        {(selectedPersonId || overdueOnly) && (
          <div className="px-6 pt-4 flex items-center gap-3">
            {selectedPersonId && (
              <div className="inline-flex items-center gap-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 px-3 py-1.5 rounded-full text-sm">
                <span>Showing items for:</span>
                <span className="font-semibold">
                  {persons.find(p => p.id === selectedPersonId)?.name}
                </span>
                <button
                  onClick={() => setSelectedPersonId(null)}
                  className="ml-1 hover:bg-indigo-200 dark:hover:bg-indigo-800 rounded-full p-0.5 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
            {overdueOnly && (
              <div className="inline-flex items-center gap-2 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-3 py-1.5 rounded-full text-sm">
                <span className="font-semibold">Showing overdue only</span>
                <button
                  onClick={() => setOverdueOnly(false)}
                  className="ml-1 hover:bg-red-200 dark:hover:bg-red-800 rounded-full p-0.5 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Reorder feedback message */}
          {reorderMessage && (
            <div className={`px-4 py-2 rounded-lg text-sm font-medium ${
              reorderMessage.type === 'success'
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {reorderMessage.text}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              {/* Sprints section */}
              {sprints.length > 0 && (
                <div className="space-y-3">
                  <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Sprints</h2>
                  {sprints.map(sprint => (
                    <div key={sprint.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-gray-900 dark:text-gray-100">{sprint.name}</h3>
                            {!selectedProjectId && sprint.project_name && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 font-medium">
                                {sprint.project_code || sprint.project_name}
                              </span>
                            )}
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              sprint.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                              sprint.status === 'planning' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                              sprint.status === 'completed' ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {sprint.status}
                            </span>
                            {sprint.team_name && (
                              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                                {sprint.team_name}
                              </span>
                            )}
                            {filteredSprintItemsMap[sprint.id] && filteredSprintItemsMap[sprint.id].length > 0 && (
                              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                                {filteredSprintItemsMap[sprint.id].length} item{filteredSprintItemsMap[sprint.id].length !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                            {new Date(sprint.start_date).toLocaleDateString()} - {new Date(sprint.end_date).toLocaleDateString()}
                            {sprint.goal && <span className="ml-2">| {sprint.goal}</span>}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          {filteredSprintItemsMap[sprint.id] && filteredSprintItemsMap[sprint.id].length > 0 && (() => {
                            const items = filteredSprintItemsMap[sprint.id];
                            const totalPoints = items.reduce((sum: number, i: any) => sum + (i.story_points || 0), 0);
                            const taskCount = items.filter((i: any) => i.story_type === 'task').length;
                            const bugCount = items.filter((i: any) => i.story_type === 'bug').length;
                            const spikeCount = items.filter((i: any) => i.story_type === 'spike').length;
                            const noTypeCount = items.filter((i: any) => !i.story_type).length;
                            return (
                              <div className="flex items-center gap-3">
                                <span className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 font-medium">
                                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white text-xs font-bold">{totalPoints}</span>
                                  pts
                                </span>
                                {taskCount > 0 && (
                                  <span className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
                                    <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
                                    {taskCount} {taskCount === 1 ? 'Task' : 'Tasks'}
                                  </span>
                                )}
                                {bugCount > 0 && (
                                  <span className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
                                    <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                                    {bugCount} {bugCount === 1 ? 'Bug' : 'Bugs'}
                                  </span>
                                )}
                                {spikeCount > 0 && (
                                  <span className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
                                    <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
                                    {spikeCount} {spikeCount === 1 ? 'Spike' : 'Spikes'}
                                  </span>
                                )}
                                {noTypeCount > 0 && (
                                  <span className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                    {noTypeCount} No Type
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                          {filteredSprintItemsMap[sprint.id] && filteredSprintItemsMap[sprint.id].length > 0 && (
                            <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-4"></div>
                          )}
                          <div className="flex items-center gap-2">
                          {sprint.status !== 'completed' && getSelectedSprintItemCount(sprint.id) > 0 && (
                            <button
                              onClick={() => handleRemoveFromSprint()}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 transition-colors"
                            >
                              Move {getSelectedSprintItemCount(sprint.id)} to Backlog
                            </button>
                          )}
                          {sprint.status === 'planning' && isManager && (
                            <>
                              {selectedItems.size > 0 && (
                                <button
                                  onClick={() => handleAddToSprint(sprint.id)}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 transition-colors"
                                >
                                  Add {selectedItems.size} item{selectedItems.size > 1 ? 's' : ''}
                                </button>
                              )}
                              <button
                                onClick={() => handleEditSprint(sprint)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 transition-colors"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleStartSprint(sprint.id)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 transition-colors"
                              >
                                Start Sprint
                              </button>
                              <button
                                onClick={() => handleDeleteSprint(sprint.id)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 border border-border-primary text-sm font-medium rounded-full text-red-600 dark:text-red-400 bg-surface-primary hover:bg-background-secondary transition-colors"
                              >
                                Delete
                              </button>
                            </>
                          )}
                          {sprint.status === 'active' && isManager && (
                            <>
                              {selectedItems.size > 0 && (
                                <button
                                  onClick={() => handleAddToSprint(sprint.id)}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 transition-colors"
                                >
                                  Add {selectedItems.size} item{selectedItems.size > 1 ? 's' : ''}
                                </button>
                              )}
                              <button
                                onClick={() => handleEditSprint(sprint)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 transition-colors"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleCompleteSprint(sprint)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 transition-colors"
                              >
                                Complete Sprint
                              </button>
                            </>
                          )}
                          </div>
                        </div>
                      </div>
                      {/* Sprint items */}
                      {filteredSprintItemsMap[sprint.id] && filteredSprintItemsMap[sprint.id].length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                          <div className="bg-gray-50 dark:bg-gray-900/30 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <table className="w-full">
                              <thead>
                                <tr className="bg-gray-200 dark:bg-gray-700 border-b border-gray-300 dark:border-gray-600">
                                  <th className="w-8 px-2 py-1.5"></th>
                                  {sprint.status !== 'completed' && (
                                    <th className="w-10 px-2 py-1.5">
                                      <input
                                        type="checkbox"
                                        checked={(filteredSprintItemsMap[sprint.id] || []).length > 0 && (filteredSprintItemsMap[sprint.id] || []).every((item: any) => selectedSprintItems.has(item.id))}
                                        onChange={() => toggleSelectAllSprintItems(sprint.id)}
                                        className="rounded text-indigo-600 focus:ring-indigo-500"
                                      />
                                    </th>
                                  )}
                                  <th className="text-left px-2 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Title</th>
                                  <th className="text-center px-2 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-24">Type</th>
                                  <th className="text-left px-2 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-20">Priority</th>
                                  <th className="text-center px-2 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-14">Points</th>
                                  <th className="text-left px-2 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-28">Assignee</th>
                                  {sprint.status !== 'completed' && <th className="w-10 px-2 py-1.5"></th>}
                                </tr>
                              </thead>
                              <Droppable droppableId={`sprint-${sprint.id}`}>
                                {(provided) => (
                                  <tbody
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    className="divide-y divide-gray-100 dark:divide-gray-700"
                                  >
                                    {filteredSprintItemsMap[sprint.id].map((item: any, index: number) => (
                                      <Draggable key={item.id} draggableId={`sprint-item-${item.id}`} index={index}>
                                        {(dragProvided, snapshot) => (
                                          <tr
                                            ref={dragProvided.innerRef}
                                            {...dragProvided.draggableProps}
                                            className={`${!snapshot.isDragging && item.is_verified === false ? 'bg-yellow-50 dark:bg-yellow-900/20' : 'bg-white dark:bg-gray-800'} hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors ${
                                              snapshot.isDragging ? 'shadow-lg ring-2 ring-indigo-500' : ''
                                            }`}
                                          >
                                            <td className="px-2 py-1.5" {...dragProvided.dragHandleProps}>
                                              <svg className="w-4 h-4 text-gray-400 cursor-grab" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                                              </svg>
                                            </td>
                                            {sprint.status !== 'completed' && (
                                              <td className="px-2 py-1.5">
                                                <input
                                                  type="checkbox"
                                                  checked={selectedSprintItems.has(item.id)}
                                                  onChange={() => toggleSprintItemSelection(item.id)}
                                                  className="rounded text-indigo-600 focus:ring-indigo-500"
                                                />
                                              </td>
                                            )}
                                            <td className="px-2 py-1.5">
                                              <button
                                                onClick={() => navigate(`/taskboard/requirement/${item.id}`)}
                                                className="text-sm text-gray-900 dark:text-gray-100 hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline text-left"
                                              >
                                                {item.requirement_code && (
                                                  <span className="text-xs font-mono font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 rounded-full border border-indigo-200 dark:border-indigo-800 mr-2">{item.requirement_code}</span>
                                                )}
                                                {item.title}
                                              </button>
                                            </td>
                                            <td className="px-2 py-1.5 text-center">
                                              {editingField?.id === item.id && editingField?.field === 'story_type' ? (
                                                <select
                                                  value={item.story_type || ''}
                                                  onChange={e => handleInlineEdit(item.id, 'story_type', e.target.value || null)}
                                                  onBlur={() => setEditingField(null)}
                                                  autoFocus
                                                  className="w-full px-1 py-0.5 text-xs border border-indigo-300 rounded dark:bg-gray-700 dark:text-gray-100 focus:outline-none"
                                                >
                                                  <option value="">None</option>
                                                  {STORY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                                </select>
                                              ) : (
                                                <button
                                                  onClick={() => isManager && setEditingField({ id: item.id, field: 'story_type' })}
                                                  className="hover:text-indigo-600 dark:hover:text-indigo-400 cursor-pointer"
                                                >
                                                  {renderStoryTypeIcon(item.story_type)}
                                                </button>
                                              )}
                                            </td>
                                            <td className="px-2 py-1.5">
                                              {editingField?.id === item.id && editingField?.field === 'priority' ? (
                                                <select
                                                  value={item.priority || ''}
                                                  onChange={e => handleInlineEdit(item.id, 'priority', e.target.value || null)}
                                                  onBlur={() => setEditingField(null)}
                                                  autoFocus
                                                  className="w-full px-1 py-0.5 text-xs border border-indigo-300 rounded dark:bg-gray-700 dark:text-gray-100 focus:outline-none"
                                                >
                                                  {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                                                </select>
                                              ) : (
                                                <button
                                                  onClick={() => setEditingField({ id: item.id, field: 'priority' })}
                                                  className="cursor-pointer"
                                                >
                                                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                                                    item.priority === 'critical' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                                    item.priority === 'high' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                                                    item.priority === 'medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                                    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                  }`}>
                                                    {item.priority}
                                                  </span>
                                                </button>
                                              )}
                                            </td>
                                            <td className="px-2 py-1.5 text-center">
                                              {editingField?.id === item.id && editingField?.field === 'story_points' ? (
                                                <input
                                                  type="number"
                                                  min="0"
                                                  defaultValue={item.story_points || ''}
                                                  onBlur={e => { handleInlineEdit(item.id, 'story_points', e.target.value ? Number(e.target.value) : null); }}
                                                  onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                                  autoFocus
                                                  className="w-14 px-1 py-0.5 text-xs border border-indigo-300 rounded dark:bg-gray-700 dark:text-gray-100 focus:outline-none text-center"
                                                />
                                              ) : (
                                                <button
                                                  onClick={() => isManager && setEditingField({ id: item.id, field: 'story_points' })}
                                                  className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white text-xs font-bold shadow-sm hover:from-indigo-600 hover:to-blue-700 cursor-pointer transition-colors"
                                                >
                                                  {item.story_points ?? '—'}
                                                </button>
                                              )}
                                            </td>
                                            <td className="px-2 py-1.5">
                                              {editingField?.id === item.id && editingField?.field === 'assigned_to' ? (
                                                <select
                                                  value={item.assigned_to || ''}
                                                  onChange={e => handleInlineEdit(item.id, 'assigned_to', e.target.value || null)}
                                                  onBlur={() => setEditingField(null)}
                                                  autoFocus
                                                  className="w-full px-1 py-0.5 text-xs border border-indigo-300 rounded dark:bg-gray-700 dark:text-gray-100 focus:outline-none"
                                                >
                                                  <option value="">Unassigned</option>
                                                  {companyUsers.map(u => (
                                                    <option key={u.id} value={u.id}>
                                                      {u.first_name} {u.last_name}
                                                    </option>
                                                  ))}
                                                </select>
                                              ) : (
                                                <button
                                                  onClick={() => isManager && setEditingField({ id: item.id, field: 'assigned_to' })}
                                                  className="inline-flex items-center gap-1.5 hover:text-indigo-600 dark:hover:text-indigo-400 cursor-pointer"
                                                >
                                                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                                                    {getAssigneeName(item.assigned_to).split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                                                  </div>
                                                  <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{getAssigneeName(item.assigned_to)}</span>
                                                </button>
                                              )}
                                            </td>
                                            {sprint.status !== 'completed' && renderActionDropdown(item, sprint.id)}
                                          </tr>
                                        )}
                                      </Draggable>
                                    ))}
                                    {provided.placeholder}
                                  </tbody>
                                )}
                              </Droppable>
                            </table>
                          </div>
                        </div>
                      )}
                      {filteredSprintItemsMap[sprint.id] && filteredSprintItemsMap[sprint.id].length === 0 && sprint.status !== 'completed' && (
                        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                          <p className="text-xs text-gray-400 italic">No items in this sprint yet</p>
                        </div>
                      )}
                      {sprint.status !== 'completed' && (
                        <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-2.5 mt-2">
                          {quickAddActiveSection === sprint.id ? (
                            <div className="flex items-center gap-2">
                              {!selectedProjectId && (
                                <select
                                  value={quickAddProjectId}
                                  onChange={e => setQuickAddProjectId(e.target.value)}
                                  className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                                >
                                  {projects.map(p => (
                                    <option key={p.id} value={p.id}>{p.project_code} - {p.name}</option>
                                  ))}
                                </select>
                              )}
                              <input
                                type="text"
                                value={quickAddTitle}
                                onChange={e => setQuickAddTitle(e.target.value)}
                                onKeyDown={e => handleQuickAddKeyDown(e, sprint.id)}
                                onBlur={() => { if (!quickAddTitle.trim()) handleQuickAddCancel(); }}
                                placeholder="Enter task title and press Enter..."
                                autoFocus
                                disabled={quickAddSubmitting}
                                className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none disabled:opacity-50"
                              />
                            </div>
                          ) : (
                            <button
                              onClick={() => handleQuickAddActivate(sprint.id)}
                              className="text-sm text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium"
                            >
                              + Add Task
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Sprint creation form (sprints are company-wide) */}
              {showCreateSprint && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                  <div className="fixed inset-0 bg-black/50" onClick={() => setShowCreateSprint(false)} />
                  <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
                    <div className="flex items-center justify-between mb-5">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Create Sprint</h3>
                      <button onClick={() => setShowCreateSprint(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sprint Name</label>
                        <input
                          type="text"
                          value={newSprintName}
                          onChange={e => setNewSprintName(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                          placeholder="e.g. Sprint 12"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Goal <span className="text-gray-400 font-normal">(optional)</span></label>
                        <input
                          type="text"
                          value={newSprintGoal}
                          onChange={e => setNewSprintGoal(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                          placeholder="What should this sprint accomplish?"
                        />
                      </div>
                      {teams.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Team</label>
                          <select
                            value={newSprintTeamId}
                            onChange={e => setNewSprintTeamId(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                          >
                            <option value="">All Teams</option>
                            {teams.map(t => (
                              <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                          <input
                            type="date"
                            value={newSprintStart}
                            onChange={e => setNewSprintStart(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
                          <input
                            type="date"
                            value={newSprintEnd}
                            onChange={e => setNewSprintEnd(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-6">
                      <button
                        onClick={() => setShowCreateSprint(false)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-surface-primary border border-border-primary rounded-full text-sm font-medium text-text-secondary hover:bg-background-secondary hover:border-gray-400 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCreateSprint}
                        disabled={creatingSprint || !newSprintName || !newSprintStart || !newSprintEnd}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {creatingSprint ? 'Creating...' : 'Create Sprint'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Edit sprint modal */}
              {editingSprint && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                  <div className="fixed inset-0 bg-black/50" onClick={() => setEditingSprint(null)} />
                  <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
                    <div className="flex items-center justify-between mb-5">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Edit Sprint</h3>
                      <button onClick={() => setEditingSprint(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sprint Name</label>
                        <input
                          type="text"
                          value={editSprintName}
                          onChange={e => setEditSprintName(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                          placeholder="e.g. Sprint 12"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Goal <span className="text-gray-400 font-normal">(optional)</span></label>
                        <input
                          type="text"
                          value={editSprintGoal}
                          onChange={e => setEditSprintGoal(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                          placeholder="What should this sprint accomplish?"
                        />
                      </div>
                      {teams.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Team</label>
                          <select
                            value={editSprintTeamId}
                            onChange={e => setEditSprintTeamId(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                          >
                            <option value="">All Teams</option>
                            {teams.map(t => (
                              <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                          <input
                            type="date"
                            value={editSprintStart}
                            onChange={e => setEditSprintStart(e.target.value)}
                            disabled={editingSprint.status === 'active'}
                            className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none ${editingSprint.status === 'active' ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-800' : ''}`}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
                          <input
                            type="date"
                            value={editSprintEnd}
                            onChange={e => setEditSprintEnd(e.target.value)}
                            disabled={editingSprint.status === 'active'}
                            className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none ${editingSprint.status === 'active' ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-800' : ''}`}
                          />
                        </div>
                      </div>
                      {editingSprint.status === 'active' && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 italic">Dates cannot be changed for active sprints</p>
                      )}
                    </div>
                    <div className="flex justify-end gap-2 mt-6">
                      <button
                        onClick={() => setEditingSprint(null)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-surface-primary border border-border-primary rounded-full text-sm font-medium text-text-secondary hover:bg-background-secondary hover:border-gray-400 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdateSprint}
                        disabled={updatingSprint || !editSprintName}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {updatingSprint ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Backlog table */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Backlog ({filteredBacklog.length})
                  </h2>
                  <div className="flex items-center gap-3">
                    {filteredBacklog.length > 0 && (() => {
                      const totalPoints = filteredBacklog.reduce((sum: number, i: any) => sum + (i.story_points || 0), 0);
                      const taskCount = filteredBacklog.filter((i: any) => i.story_type === 'task').length;
                      const bugCount = filteredBacklog.filter((i: any) => i.story_type === 'bug').length;
                      const spikeCount = filteredBacklog.filter((i: any) => i.story_type === 'spike').length;
                      const noTypeCount = filteredBacklog.filter((i: any) => !i.story_type).length;
                      return (
                        <div className="flex items-center gap-3">
                          <span className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 font-medium">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white text-xs font-bold">{totalPoints}</span>
                            pts
                          </span>
                          {taskCount > 0 && (
                            <span className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
                              <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
                              {taskCount} {taskCount === 1 ? 'Task' : 'Tasks'}
                            </span>
                          )}
                          {bugCount > 0 && (
                            <span className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
                              <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                              {bugCount} {bugCount === 1 ? 'Bug' : 'Bugs'}
                            </span>
                          )}
                          {spikeCount > 0 && (
                            <span className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
                              <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
                              {spikeCount} {spikeCount === 1 ? 'Spike' : 'Spikes'}
                            </span>
                          )}
                          {noTypeCount > 0 && (
                            <span className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                              {noTypeCount} No Type
                            </span>
                          )}
                        </div>
                      );
                    })()}
                    {filteredBacklog.length > 0 && (
                      <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-4"></div>
                    )}
                    <select
                      value={filterPriority}
                      onChange={e => setFilterPriority(e.target.value)}
                      className="pl-2 pr-7 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-gray-100 focus:outline-none"
                    >
                      <option value="">All Priorities</option>
                      {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                    </select>
                    <select
                      value={filterStoryType}
                      onChange={e => setFilterStoryType(e.target.value)}
                      className="pl-2 pr-7 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-gray-100 focus:outline-none"
                    >
                      <option value="">All Types</option>
                      {STORY_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                    </select>
                  </div>
                </div>

                {filteredBacklog.length === 0 ? (
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="p-8 text-center text-gray-500">
                      <p className="text-lg font-medium">No backlog items</p>
                      <p className="text-sm mt-1">Approved requirements will appear here when they have no sprint assigned.</p>
                    </div>
                    <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-2.5">
                      {quickAddActiveSection === 'backlog' ? (
                        <div className="flex items-center gap-2">
                          {!selectedProjectId && (
                            <select
                              value={quickAddProjectId}
                              onChange={e => setQuickAddProjectId(e.target.value)}
                              className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                            >
                              {projects.map(p => (
                                <option key={p.id} value={p.id}>{p.project_code} - {p.name}</option>
                              ))}
                            </select>
                          )}
                          <input
                            type="text"
                            value={quickAddTitle}
                            onChange={e => setQuickAddTitle(e.target.value)}
                            onKeyDown={e => handleQuickAddKeyDown(e, 'backlog')}
                            onBlur={() => { if (!quickAddTitle.trim()) handleQuickAddCancel(); }}
                            placeholder="Enter task title and press Enter..."
                            autoFocus
                            disabled={quickAddSubmitting}
                            className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none disabled:opacity-50"
                          />
                        </div>
                      ) : (
                        <button
                          onClick={() => handleQuickAddActivate('backlog')}
                          className="text-sm text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium"
                        >
                          + Add Task
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-200 dark:bg-gray-700 border-b border-gray-300 dark:border-gray-600">
                          <th className="w-8 px-2 py-2"></th>
                          <th className="w-10 px-3 py-2">
                            <input
                              type="checkbox"
                              checked={selectedItems.size === filteredBacklog.length && filteredBacklog.length > 0}
                              onChange={toggleSelectAll}
                              className="rounded text-indigo-600 focus:ring-indigo-500"
                            />
                          </th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Title</th>
                          <th className="text-center px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-24">Type</th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-24">Priority</th>
                          <th className="text-center px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-16">Points</th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-32">Assignee</th>
                          <th className="w-10 px-3 py-2"></th>
                        </tr>
                      </thead>
                      <Droppable droppableId="backlog">
                        {(provided) => (
                          <tbody
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className="divide-y divide-gray-100 dark:divide-gray-700"
                          >
                            {filteredBacklog.map((item, index) => (
                              <Draggable key={item.id} draggableId={`backlog-${item.id}`} index={index}>
                                {(dragProvided, snapshot) => (
                                  <tr
                                    ref={dragProvided.innerRef}
                                    {...dragProvided.draggableProps}
                                    className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors ${
                                      snapshot.isDragging ? 'bg-indigo-50 dark:bg-indigo-900/20 shadow-lg ring-2 ring-indigo-500' : ''
                                    } ${!snapshot.isDragging && item.is_verified === false ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''}`}
                                  >
                                    <td className="px-2 py-2" {...dragProvided.dragHandleProps}>
                                      <svg className="w-4 h-4 text-gray-400 cursor-grab" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                                      </svg>
                                    </td>
                                    <td className="px-3 py-2">
                                      <input
                                        type="checkbox"
                                        checked={selectedItems.has(item.id)}
                                        onChange={() => toggleSelection(item.id)}
                                        className="rounded text-indigo-600 focus:ring-indigo-500"
                                      />
                                    </td>
                                    <td className="px-3 py-2">
                                      <button
                                        onClick={() => navigate(`/taskboard/requirement/${item.id}`)}
                                        className="text-sm text-gray-900 dark:text-gray-100 hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline text-left"
                                      >
                                        {item.requirement_code && (
                                          <span className="text-xs font-mono font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 rounded-full border border-indigo-200 dark:border-indigo-800 mr-2">{item.requirement_code}</span>
                                        )}
                                        {item.title}
                                      </button>
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      {editingField?.id === item.id && editingField?.field === 'story_type' ? (
                                        <select
                                          value={item.story_type || ''}
                                          onChange={e => handleInlineEdit(item.id, 'story_type', e.target.value || null)}
                                          onBlur={() => setEditingField(null)}
                                          autoFocus
                                          className="w-full px-1 py-0.5 text-xs border border-indigo-300 rounded dark:bg-gray-700 dark:text-gray-100 focus:outline-none"
                                        >
                                          <option value="">None</option>
                                          {STORY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                      ) : (
                                        <button
                                          onClick={() => isManager && setEditingField({ id: item.id, field: 'story_type' })}
                                          className="hover:text-indigo-600 dark:hover:text-indigo-400 cursor-pointer"
                                        >
                                          {renderStoryTypeIcon(item.story_type)}
                                        </button>
                                      )}
                                    </td>
                                    <td className="px-3 py-2">
                                      {editingField?.id === item.id && editingField?.field === 'priority' ? (
                                        <select
                                          value={item.priority || ''}
                                          onChange={e => handleInlineEdit(item.id, 'priority', e.target.value || null)}
                                          onBlur={() => setEditingField(null)}
                                          autoFocus
                                          className="w-full px-1 py-0.5 text-xs border border-indigo-300 rounded dark:bg-gray-700 dark:text-gray-100 focus:outline-none"
                                        >
                                          {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                                        </select>
                                      ) : (
                                        <button
                                          onClick={() => setEditingField({ id: item.id, field: 'priority' })}
                                          className="cursor-pointer"
                                        >
                                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                            item.priority === 'critical' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                            item.priority === 'high' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                                            item.priority === 'medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                            'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                          }`}>
                                            {item.priority}
                                          </span>
                                        </button>
                                      )}
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      {editingField?.id === item.id && editingField?.field === 'story_points' ? (
                                        <input
                                          type="number"
                                          min="0"
                                          defaultValue={item.story_points || ''}
                                          onBlur={e => { handleInlineEdit(item.id, 'story_points', e.target.value ? Number(e.target.value) : null); }}
                                          onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                          autoFocus
                                          className="w-14 px-1 py-0.5 text-xs border border-indigo-300 rounded dark:bg-gray-700 dark:text-gray-100 focus:outline-none text-center"
                                        />
                                      ) : (
                                        <button
                                          onClick={() => isManager && setEditingField({ id: item.id, field: 'story_points' })}
                                          className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white text-xs font-bold shadow-sm hover:from-indigo-600 hover:to-blue-700 cursor-pointer transition-colors"
                                        >
                                          {item.story_points ?? '—'}
                                        </button>
                                      )}
                                    </td>
                                    <td className="px-3 py-2">
                                      {editingField?.id === item.id && editingField?.field === 'assigned_to' ? (
                                        <select
                                          value={item.assigned_to || ''}
                                          onChange={e => handleInlineEdit(item.id, 'assigned_to', e.target.value || null)}
                                          onBlur={() => setEditingField(null)}
                                          autoFocus
                                          className="w-full px-1 py-0.5 text-xs border border-indigo-300 rounded dark:bg-gray-700 dark:text-gray-100 focus:outline-none"
                                        >
                                          <option value="">Unassigned</option>
                                          {companyUsers.map(u => (
                                            <option key={u.id} value={u.id}>
                                              {u.first_name} {u.last_name}
                                            </option>
                                          ))}
                                        </select>
                                      ) : (
                                        <button
                                          onClick={() => isManager && setEditingField({ id: item.id, field: 'assigned_to' })}
                                          className="inline-flex items-center gap-1.5 hover:text-indigo-600 dark:hover:text-indigo-400 cursor-pointer"
                                        >
                                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                                            {(item.assigned_to_name || 'Unassigned').split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)}
                                          </div>
                                          <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{item.assigned_to_name || 'Unassigned'}</span>
                                        </button>
                                      )}
                                    </td>
                                    {renderActionDropdown(item)}
                                  </tr>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </tbody>
                        )}
                      </Droppable>
                    </table>
                    <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-2.5">
                      {quickAddActiveSection === 'backlog' ? (
                        <div className="flex items-center gap-2">
                          {!selectedProjectId && (
                            <select
                              value={quickAddProjectId}
                              onChange={e => setQuickAddProjectId(e.target.value)}
                              className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                            >
                              {projects.map(p => (
                                <option key={p.id} value={p.id}>{p.project_code} - {p.name}</option>
                              ))}
                            </select>
                          )}
                          <input
                            type="text"
                            value={quickAddTitle}
                            onChange={e => setQuickAddTitle(e.target.value)}
                            onKeyDown={e => handleQuickAddKeyDown(e, 'backlog')}
                            onBlur={() => { if (!quickAddTitle.trim()) handleQuickAddCancel(); }}
                            placeholder="Enter task title and press Enter..."
                            autoFocus
                            disabled={quickAddSubmitting}
                            className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none disabled:opacity-50"
                          />
                        </div>
                      ) : (
                        <button
                          onClick={() => handleQuickAddActivate('backlog')}
                          className="text-sm text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium"
                        >
                          + Add Task
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </DragDropContext>
          )}
        </div>
      </div>

      {/* Sprint completion modal */}
      {completingSprint && (
        <SprintCompletionModal
          sprint={completingSprint}
          incompleteItems={sprintItems}
          availableSprints={sprints.filter(s => s.id !== completingSprint.id && s.status !== 'completed')}
          onClose={() => setCompletingSprint(null)}
          onComplete={() => {
            setCompletingSprint(null);
            loadData();
          }}
        />
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Delete Task</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete <span className="font-medium text-gray-900 dark:text-gray-100">"{confirmDelete.title}"</span>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default TaskBoardBacklog;
